<template>
  <div class="shipping-checkpoint-form-address">
    <!-- all clear -->
    <div v-if="!shippingEdit.fields_revision || (shippingEdit.fields_revision && !shippingEdit.fields_revision_data[checkpoint])">
      <label for="address" v-translate>Street and number</label>
      <address-autocomplete :disabled="disabled" id="address" ref="address" v-if="newAddress || shippingEdit['address_' + checkpoint]" :pre-query="!newAddress ? shippingEdit['address_' + checkpoint].formatted_address : ''" @selected="selected" @input="shippingEdit[checkpoint + '_place_id'] = null" static-items :minCharacter="4" />
      <b-form-text v-if="!disabled" v-translate>We use Google to validate all addresses, please keep it in mind when you enter yours.</b-form-text>
      <b-form-text v-else><span class="text-danger" v-translate>The address cannot be changed at this point of the process.</span></b-form-text>
    </div>

    <!-- has some not clear string -->
    <div v-if="originalString">
      <h6 v-translate>Original Data</h6>
      <p>{{originalString}}
        <a href="#" @click.prevent="copyToClipboard(originalString, 'text')" class="text-secondary" title="Copy to Clipboard"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
        <b-button size="sm" v-b-modal.modalReviewAddressMap @click.stop="openMapOn(originalString)" class="py-0 px-1" :title="$gettext('Quick view address map on new window')">
          <font-awesome-icon :icon="['fa', 'map-marked-alt']" size="1x" />
        </b-button>
      </p>
    </div>

    <!-- yellow -->
    <div v-if="shippingEdit.fields_revision_data &&
      shippingEdit.fields_revision_data[checkpoint] &&
      typeof shippingEdit.fields_revision_data[checkpoint] === 'object'">
      <div v-show="editType === 'radio'">
        <b-form-radio-group id="radio-group-2" v-model="shippingEdit[checkpoint + '_place_id']" @input="theCheckedAddress.id = shippingEdit[checkpoint + '_place_id']; setPlace();">
          <b-form-radio :value="item.place_id" v-for="(item, index) in shippingEdit.fields_revision_data[checkpoint]" :key="index" :selected="item.place_id === shippingEdit[checkpoint + '_place_id']">
            {{ item.main_text }}, {{ item.secondary_text }}
            <b-button size="sm" v-b-modal.modalReviewAddressMap @click.stop="openMapOn(item.main_text + ', ' + item.secondary_text)" class="py-0 px-1" :title="$gettext('Quick view address map on new window')">
              <font-awesome-icon :icon="['fa', 'map-marked-alt']" />
            </b-button>
          </b-form-radio>
        </b-form-radio-group>
        <b-button size="sm" style="padding: .25rem 1rem" variant="primary" @click.prevent="editType = 'autocomplete'" class="mt-3">
          <translate>Change to Autocomplete</translate>
        </b-button>
      </div>
      <div v-show="editType === 'autocomplete'">
        <address-autocomplete id="address" ref="address" @selected="selected" @input="shippingEdit[checkpoint + '_place_id'] = null" static-items :minCharacter="4" />
        <b-button size="sm" style="padding: .25rem 1rem" variant="primary" @click.prevent="editType = 'radio'" class="mt-3">
          <translate>Change to Multiple</translate>
        </b-button>
      </div>
    </div>

    <!-- red -->
    <div v-if="shippingEdit.fields_revision_data &&
      shippingEdit.fields_revision_data[checkpoint] &&
      typeof shippingEdit.fields_revision_data[checkpoint] === 'string'">
      <dl>
        <dt v-translate>Current failed string</dt>
        <dd>
          {{shippingEdit.fields_revision_data[checkpoint]}}
          <a href="#" @click.prevent="copyToClipboard(shippingEdit.fields_revision_data[checkpoint], 'text')" class="text-secondary" title="Copy to Clipboard"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
          <b-button size="sm" v-b-modal.modalReviewAddressMap @click.stop="openMapOn(shippingEdit.fields_revision_data[checkpoint])" class="py-0 px-1" :title="$gettext('Quick view address map on new window')">
            <font-awesome-icon :icon="['fa', 'map-marked-alt']" />
          </b-button>
        </dd>
      </dl>
      <address-autocomplete id="address" ref="address" @selected="selected" @input="shippingEdit[checkpoint + '_place_id'] = null" static-items :minCharacter="4" />
    </div>

    <b-modal size="lg" id="modalPickPlace" ref="modalPickPlace" hide-footer hide-header @hide="$refs.pickPlaceMap.pickPlace(0, 0)">
      <place-picker ref="pickPlaceMap" @cancel="closeModalPickPlace" @placePicked="setPlacePicked" centerMap fit-always :zoom="17" hasReference />
    </b-modal>
  </div>
</template>

<script>
import AddressMixins from '@/mixins/address.mixin'
import AddressAutocomplete from '@/components/AddressAutocomplete'
import PlacePicker from '@/components/PlacePicker'

export default {
  name: 'ShippingCheckpointFormAddress',
  components: {
    AddressAutocomplete,
    PlacePicker
  },
  props: {
    shipping: {
      type: Object,
      default: () => {},
      required: true
    },
    checkpoint: {
      type: String,
      default: 'to'
    },
    newAddress: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      shippingEdit: {},
      editType: 'radio'
    }
  },
  computed: {
    originalString() {
      if (this.shippingEdit.fields_revision_data && this.shippingEdit.fields_revision_data[this.checkpoint + '_original']) {
        if (this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].address) {
          return this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].address
        } else {
          let str = ''
          str += this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].number ? this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].number + ' ' : ''
          str += this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].street ? this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].street + ',' : ''
          str += this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].city ? this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].city + ',' : ''
          str += this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].state ? this.shippingEdit.fields_revision_data[this.checkpoint + '_original'].state + ',' : ''
          str += window.countries.find(x => x.iso_code === (this.shippingEdit.company ? this.shippingEdit.company.country_iso_code : (this.shippingEdit.from_country_iso_code ? this.shippingEdit.from_country_iso_code : this.shippingEdit.to_country_iso_code))).name
          return str
        }
      }
    }
  },
  mixins: [AddressMixins],
  mounted() {
    this.shippingEdit = { ...this.shipping }
  },
  methods: {
    async selected(place) {
      if (place.reference) {
        this.shippingEdit[this.checkpoint + '_contact'] = place.contact
        this.shippingEdit[this.checkpoint + '_email'] = place.email
        this.shippingEdit[this.checkpoint + '_phone'] = place.phone
        this.shippingEdit[this.checkpoint + '_extra'] = place.address_extra
        await this.checkPlace({ id: place.place_id, main_text: place.place.formatted_address, secondary_text: '' })
          .then(() => {
            this.setPlace()
          })
      } else {
        await this.checkPlace(place)
          .then(() => {
            this.setPlace()
          })
      }
      this.$nextTick(() => {
        if (this.theCheckedAddress.id) {
          this.setPlace()
          this.theCheckedAddress = {}
        } else if (this.theCheckedAddress.validAddress === false) {
          this.$refs.modalPickPlace.show()
          setTimeout(function () {
            this.$refs.pickPlaceMap.center = {
              lat: this.theCheckedAddress.lat,
              lng: this.theCheckedAddress.lng
            }
            this.$refs.pickPlaceMap.reference = this.$refs.address ? this.$refs.address.searchQuery : this.$refs.acToPlaceId.searchQuery
            this.$refs.pickPlaceMap.styles = 'height: 500px;'
          }.bind(this), 100)
        }
      })
    },
    setPlace() {
      if (this.theCheckedAddress.id) {
        this.$emit('success', { [this.checkpoint + '_place_id']: this.theCheckedAddress.id, [this.checkpoint + '_formatted_address']: this.theCheckedAddress.formatted_address, force_place_id: true })
        this.shippingEdit[this.checkpoint + '_place_id'] = this.theCheckedAddress.id
        this.shippingEdit[this.checkpoint + '_formatted_address'] = this.theCheckedAddress.formatted_address
      }
    },
    setPlacePicked(place) {
      this.shippingEdit[this.checkpoint + '_place_id'] = place.id
      this.$emit('success', { [this.checkpoint + '_place_id']: place.id, [this.checkpoint + '_meta']: this.$gettextInterpolate(this.$gettext('Reference: %{address}'), { address: place.reference }) + '\n\n' + this.shippingEdit[this.checkpoint + '_meta'] })
      this.shippingEdit[this.checkpoint + '_meta'] = this.$gettextInterpolate(this.$gettext('Reference: %{address}'), { address: place.reference }) + '\n\n' + this.shippingEdit[this.checkpoint + '_meta']
      this.$refs.address.searchQuery = place.formatted_address
      this.closeModalPickPlace()
    },
    closeModalPickPlace() {
      this.$refs.modalPickPlace.hide()
    },
    emitSuccess() {
      this.$emit('success', true)
    },
    emitCancel() {
      this.$emit('cancel', true)
    }
  }
}
</script>
